/**
 * Search — PHACKTS command palette.
 *
 * Merged feature set:
 *   FROM FULL-FEATURED VERSION:
 *     ✓ Unicode NFD normalization (résumé ↔ resume, naïve ↔ naive)
 *     ✓ Relevance-ranked sort (prefix boost + shorter-is-better)
 *     ✓ Deep content indexing (via buildSearchIndex in constants.ts)
 *     ✓ Search highlight flash on navigated target (2s emerald border)
 *     ✓ Action href protocol (action:toggle-theme, etc.)
 *     ✓ Tab/Shift+Tab keyboard navigation alongside arrows
 *     ✓ stopPropagation on shortcut matches
 *
 *   FROM OUR SSR VERSION (preserved):
 *     ✓ Full ARIA combobox pattern (role, expanded, controls, activedescendant)
 *     ✓ CSS transitions — no Framer Motion dependency
 *     ✓ Mobile-optimized layout (tighter padding, smaller text)
 *     ✓ Content-driven strings from SEARCH_UI
 *     ✓ SearchFeature modal integration (onSearchFeatureOpen)
 *     ✓ Regex-safe HighlightText
 *     ✓ useCallback memoization
 *     ✓ requestAnimationFrame focus management
 *     ✓ min-w-0 overflow prevention on input
 *
 * Zero hardcoded prose. GPL-licensed shell.
 */

import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { buildSearchIndex, SEARCH_UI } from '../constants';

interface SearchProps {
  isOpen: boolean;
  onClose: () => void;
  onSearchFeatureOpen: () => void;
  /** Optional callback for action: protocol hrefs (e.g., action:toggle-theme). */
  onAction?: (action: string) => void;
}

/* ================================================================
 * Unicode NFD Normalization
 *
 * Decomposes accented characters into base form before comparison.
 * "francais" matches "Français", "resume" matches "Résumé",
 * "naive" matches "Naïve" — no special typing required.
 * ================================================================ */
function normalize(str: string): string {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

/* ================================================================
 * HighlightText — Regex-safe search match highlighting
 * ================================================================ */
function HighlightText({ text, highlight }: { text: string; highlight: string }) {
  if (!highlight.trim()) return <>{text}</>;
  const escaped = highlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === highlight.toLowerCase() ? (
          <mark key={i} className="bg-emerald-500 text-white rounded-sm px-0.5">
            {part}
          </mark>
        ) : (
          part
        )
      )}
    </>
  );
}

/* ================================================================
 * Icons — inline SVGs, no library dependency
 * ================================================================ */
const ICONS = {
  doc: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
    </svg>
  ),
  content: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  action: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  ),
  arrow: (
    <svg className="h-4 w-4 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
    </svg>
  ),
  search: (
    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  ),
  empty: (
    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  ),
};

function getIconForType(type: string) {
  switch (type) {
    case 'Action': return ICONS.action;
    case 'Content': return ICONS.content;
    default: return ICONS.doc;
  }
}

/* ================================================================
 * Search Component
 * ================================================================ */
export default function Search({ isOpen, onClose, onSearchFeatureOpen, onAction }: SearchProps) {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  // Build the full search corpus (navigation + actions + deep content)
  const searchIndex = useMemo(() => buildSearchIndex(), []);

  /* ── Filtering + Relevance Ranking ── */
  const filteredResults = useMemo(() => {
    const q = normalize(query);
    if (!q) return searchIndex.slice(0, 12);

    return searchIndex
      .filter((item) => {
        const titleMatch = normalize(item.title).includes(q);
        const categoryMatch = normalize(item.category).includes(q);
        return titleMatch || categoryMatch;
      })
      .sort((a, b) => {
        const aTitle = normalize(a.title);
        const bTitle = normalize(b.title);

        // Boost: prefix matches rank above substring matches
        const aStarts = aTitle.startsWith(q);
        const bStarts = bTitle.startsWith(q);
        if (aStarts && !bStarts) return -1;
        if (!aStarts && bStarts) return 1;

        // Boost: Actions rank above Content for equal relevance
        const typeOrder = { Action: 0, Navigation: 1, Content: 2 };
        const aType = typeOrder[a.type as keyof typeof typeOrder] ?? 2;
        const bType = typeOrder[b.type as keyof typeof typeOrder] ?? 2;
        if (aType !== bType) return aType - bType;

        // Tiebreak: shorter titles are more precise
        return a.title.length - b.title.length;
      });
  }, [query, searchIndex]);

  // Reset selection when results change
  useEffect(() => { setSelectedIndex(0); }, [filteredResults]);

  // Scroll active item into view
  useEffect(() => {
    itemRefs.current[selectedIndex]?.scrollIntoView({
      block: 'nearest',
      behavior: 'smooth',
    });
  }, [selectedIndex]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [isOpen]);

  const closeAndReset = useCallback(() => {
    setQuery('');
    onClose();
  }, [onClose]);

  /* ── Navigation with highlight flash ── */
  const performNavigation = useCallback(
    (href: string) => {
      // Action protocol: action:toggle-theme, action:print, etc.
      if (href.startsWith('action:')) {
        const action = href.replace('action:', '');
        onAction?.(action);
        closeAndReset();
        return;
      }

      // Internal anchor navigation with highlight
      if (href.startsWith('#')) {
        const el = document.getElementById(href.slice(1));
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });

          // Highlight the target element with a 2-second emerald border flash
          el.setAttribute('tabindex', '-1');
          el.focus({ preventScroll: true });
          el.classList.add('search-highlight');
          setTimeout(() => {
            el.classList.remove('search-highlight');
            el.removeAttribute('tabindex');
          }, 2000);
        }
      } else {
        // External link
        window.open(href, '_blank', 'noopener,noreferrer');
      }

      closeAndReset();
    },
    [closeAndReset, onAction]
  );

  /* ── Keyboard handling ── */
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          closeAndReset();
          break;

        // Arrow keys AND Tab cycle through results
        case 'ArrowDown':
        case 'Tab':
          if (e.key === 'Tab' && e.shiftKey) {
            // Shift+Tab = up
            e.preventDefault();
            setSelectedIndex((prev) =>
              filteredResults.length > 0
                ? (prev - 1 + filteredResults.length) % filteredResults.length
                : 0
            );
          } else {
            e.preventDefault();
            setSelectedIndex((prev) =>
              filteredResults.length > 0
                ? (prev + 1) % filteredResults.length
                : 0
            );
          }
          break;

        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) =>
            filteredResults.length > 0
              ? (prev - 1 + filteredResults.length) % filteredResults.length
              : 0
          );
          break;

        case 'Enter':
          e.preventDefault();
          if (filteredResults[selectedIndex]) {
            performNavigation(filteredResults[selectedIndex].href);
          }
          break;

        default:
          // Single-key shortcuts when input is empty
          if (!e.metaKey && !e.ctrlKey && query === '') {
            const key = e.key.toUpperCase();
            const match = searchIndex.find((r) => r.shortcut === key);
            if (match) {
              e.preventDefault();
              e.stopPropagation();
              performNavigation(match.href);
            }
          }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closeAndReset, filteredResults, selectedIndex, query, performNavigation, searchIndex]);

  /* ── Render ── */
  return (
    <div
      className={`search-overlay fixed inset-0 z-[100] flex items-start justify-center pt-24 px-4 sm:pt-[15vh] ${
        isOpen ? 'is-open' : ''
      }`}
      role="dialog"
      aria-modal="true"
      aria-label="Search documentation"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-slate-950/90 backdrop-blur-md"
        onClick={closeAndReset}
        aria-hidden="true"
      />

      {/* Panel */}
      <div className="search-panel relative w-full max-w-2xl overflow-hidden rounded-[2rem] bg-slate-900 border border-slate-800 shadow-2xl flex flex-col">
        {/* Search input */}
        <div className="flex items-center gap-2 sm:gap-4 px-3 sm:px-6 py-4 sm:py-5 border-b border-slate-800 bg-slate-900/50 shrink-0">
          <div className="flex items-center justify-center w-5 sm:w-6 h-5 sm:h-6 text-emerald-500 shrink-0">
            {ICONS.search}
          </div>
          <input
            ref={inputRef}
            type="text"
            placeholder={SEARCH_UI.placeholder}
            className="flex-1 min-w-0 bg-transparent text-base sm:text-lg text-white placeholder-slate-600 focus:outline-none font-medium"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            role="combobox"
            aria-expanded={filteredResults.length > 0}
            aria-controls="search-results"
            aria-activedescendant={
              filteredResults[selectedIndex]
                ? `search-result-${selectedIndex}`
                : undefined
            }
          />
          <button
            type="button"
            onClick={closeAndReset}
            className="px-2 py-1 text-[10px] font-black text-emerald-400 border border-emerald-500/40 bg-emerald-500/5 rounded-lg uppercase tracking-wider hover:bg-emerald-500/10 transition-colors shrink-0"
            aria-label="Close search"
          >
            Esc
          </button>
        </div>

        {/* Results */}
        <div
          ref={scrollRef}
          id="search-results"
          role="listbox"
          className="max-h-[50vh] overflow-y-auto p-3 space-y-1 custom-scrollbar bg-slate-900/20"
        >
          {filteredResults.length > 0 ? (
            filteredResults.map((result, index) => {
              const isSelected = index === selectedIndex;
              return (
                <button
                  key={`${result.href}-${index}`}
                  id={`search-result-${index}`}
                  ref={(el) => { itemRefs.current[index] = el; }}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => performNavigation(result.href)}
                  onMouseMove={() => setSelectedIndex(index)}
                  className={`w-full flex items-center gap-4 p-4 rounded-2xl transition-all duration-200 group border text-left ${
                    isSelected
                      ? 'bg-emerald-500/10 border-emerald-500/30 shadow-[inset_0_1px_1px_rgba(255,255,255,0.05)]'
                      : 'bg-transparent border-transparent hover:bg-slate-800/40'
                  }`}
                >
                  <div
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border transition-colors ${
                      isSelected
                        ? 'bg-emerald-500 text-white border-emerald-400/50 shadow-lg shadow-emerald-500/20'
                        : 'bg-slate-950 text-slate-500 border-slate-800'
                    }`}
                  >
                    {getIconForType(result.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div
                      className={`text-[14px] font-bold transition-colors truncate ${
                        isSelected ? 'text-white' : 'text-slate-300'
                      }`}
                    >
                      <HighlightText text={result.title} highlight={query} />
                    </div>
                    <div className="text-[10px] text-slate-500 font-mono uppercase tracking-[0.15em]">
                      {result.category}
                    </div>
                  </div>
                  <div
                    className={`flex items-center gap-3 shrink-0 ml-2 transition-opacity duration-200 ${
                      isSelected ? 'opacity-100' : 'opacity-0'
                    }`}
                  >
                    {result.shortcut && (
                      <span className="text-[9px] font-black px-1.5 py-0.5 rounded border border-emerald-500/30 text-emerald-500/60 uppercase tracking-widest">
                        {result.shortcut}
                      </span>
                    )}
                    {ICONS.arrow}
                  </div>
                </button>
              );
            })
          ) : (
            <div className="py-20 text-center" role="status">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-950 border border-slate-800 text-slate-700 mb-4">
                {ICONS.empty}
              </div>
              <p className="text-slate-400 text-sm font-bold">
                {SEARCH_UI.noResults} &ldquo;{query}&rdquo;
              </p>
              <p className="text-slate-600 text-[11px] mt-2 font-medium">
                {SEARCH_UI.noResultsSub}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-3 sm:px-6 py-3 sm:py-4 bg-slate-950/40 border-t border-slate-800 flex items-center justify-between shrink-0">
          <div className="flex gap-4 sm:gap-6">
            <div className="flex items-center gap-2">
              <kbd className="rounded bg-slate-800 px-1.5 py-0.5 text-[9px] font-black text-slate-500 border border-slate-700">
                Enter
              </kbd>
              <span className="text-[9px] font-black text-slate-600 uppercase tracking-widest">
                to select
              </span>
            </div>
            <div className="flex items-center gap-2">
              <kbd className="rounded bg-slate-800 px-1.5 py-0.5 text-[9px] font-black text-slate-500 border border-slate-700">
                ↑↓
              </kbd>
              <span className="text-[9px] font-black text-slate-600 uppercase tracking-widest">
                {SEARCH_UI.browseLabel}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              closeAndReset();
              onSearchFeatureOpen();
            }}
            className="hidden sm:inline-flex items-center gap-2 group/footer"
          >
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500/40 animate-pulse group-hover/footer:bg-emerald-500 transition-colors" aria-hidden="true" />
            <span className="text-[10px] font-black uppercase tracking-[0.2em] transition-all text-emerald-500/30 group-hover/footer:text-emerald-400 search-footer-glow">
              {SEARCH_UI.footerLabel}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
